/**
 * Brand assets for Enterprise Support Bank ("ESB") — pure inline SVG, no icon
 * library or external fetch (keeps the bundle dependency-free).
 *
 * {@link BrandLogo} renders the shield/chevron mark (teal body + gold "backing"
 * stripe, suggesting protection and support) alongside the wordmark. The mark
 * is decorative on its own but the whole lockup is exposed as an accessible
 * image via `role="img"` and a `<title>`. Size is driven by CSS (the SVG scales
 * to its container height), so the same component is crisp at ~40px in a header
 * bar and large on the login hero.
 *
 * {@link LockGlyph} and {@link ShieldGlyph} are tiny standalone marks reused for
 * the security-reassurance line and trust bullets. They are marked
 * `aria-hidden` because their meaning is always carried by adjacent text.
 */

import type { ReactNode } from 'react';

interface BrandLogoProps {
  /** Render the wordmark text next to the mark (default `true`). */
  readonly showWordmark?: boolean;
  /** Use light-on-dark colours for placement on the teal hero (default `false`). */
  readonly variant?: 'ink' | 'onDark';
  /** Optional extra class for sizing/layout. */
  readonly className?: string;
}

/** The ESB shield-chevron mark plus wordmark, as an accessible inline SVG lockup. */
export function BrandLogo({
  showWordmark = true,
  variant = 'ink',
  className,
}: BrandLogoProps): JSX.Element {
  const wordmarkColor = variant === 'onDark' ? 'var(--esb-on-primary)' : 'var(--esb-ink)';
  const classes = ['esb-logo', variant === 'onDark' ? 'esb-logo--on-dark' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} role="img" aria-label="Enterprise Support Bank">
      <svg
        className="esb-logo__mark"
        viewBox="0 0 48 56"
        width="48"
        height="56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        {/* Shield body — deeper teal so the white chevron keeps strong contrast
            against the brighter brand teal. */}
        <path
          d="M24 2 4 10v18c0 12.5 8.2 21.6 20 26 11.8-4.4 20-13.5 20-26V10L24 2Z"
          fill="var(--esb-primary-700)"
        />
        {/* Inner facet for depth — the bright brand teal, kept on one half only. */}
        <path
          d="M24 2 4 10v18c0 12.5 8.2 21.6 20 26V2Z"
          fill="var(--esb-primary)"
          fillOpacity="0.55"
        />
        {/* Upward chevron = "backing you / support" */}
        <path
          d="M24 16 13 30h6.6L24 24l4.4 6H35L24 16Z"
          fill="#FFFFFF"
        />
        {/* Gold accent stripe under the chevron */}
        <rect x="14" y="34" width="20" height="4" rx="2" fill="var(--esb-gold)" />
      </svg>
      {showWordmark ? (
        <span className="esb-logo__wordmark" style={{ color: wordmarkColor }}>
          <span className="esb-logo__name">Enterprise Support Bank</span>
        </span>
      ) : null}
    </span>
  );
}

/** A small padlock glyph for the security-reassurance line. Decorative. */
export function LockGlyph({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.5" y="7" width="11" height="7" rx="1.5" fill="currentColor" />
      <path
        d="M5 7V5a3 3 0 0 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="8" cy="10.5" r="1.25" fill="#FFFFFF" />
    </svg>
  );
}

/** A small shield/check glyph for trust bullet points. Decorative. */
export function ShieldGlyph({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M10 1.5 3 4.5v5c0 4.4 3 7.6 7 9 4-1.4 7-4.6 7-9v-5l-7-3Z"
        fill="currentColor"
      />
      <path
        d="m6.75 9.75 2.25 2.25 4.25-4.5"
        stroke="#FFFFFF"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* UI icon set — small, dependency-free inline SVGs used by the dashboard      */
/* shell (sidebar nav), the quick-action tiles, and the transaction feed       */
/* category circles. All are decorative (`aria-hidden`) because adjacent text   */
/* always carries the meaning; size is driven by the parent via `width`/       */
/* `height: 1em` so they scale with the surrounding type.                       */
/* -------------------------------------------------------------------------- */

/** Props shared by every glyph in the UI icon set. */
interface GlyphProps {
  /** Optional extra class for colour/sizing. */
  readonly className?: string;
}

/** Common wrapper that stamps out a 24×24 decorative icon with `currentColor`. */
function Glyph({
  className,
  children,
  viewBox = '0 0 24 24',
}: {
  readonly className?: string;
  readonly children: ReactNode;
  readonly viewBox?: string;
}): JSX.Element {
  return (
    <svg
      className={className}
      viewBox={viewBox}
      width="24"
      height="24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Dashboard / accounts — stacked cards. */
export function IconDashboard({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <rect x="3" y="4" width="18" height="6" rx="2" fill="currentColor" />
      <rect x="3" y="13" width="11" height="7" rx="2" fill="currentColor" opacity="0.6" />
      <rect x="16" y="13" width="5" height="7" rx="2" fill="currentColor" opacity="0.6" />
    </Glyph>
  );
}

/** Beneficiaries — people. */
export function IconPeople({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <circle cx="9" cy="8" r="3.2" fill="currentColor" />
      <path d="M3 19c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5Z" fill="currentColor" />
      <circle cx="17" cy="8.5" r="2.6" fill="currentColor" opacity="0.6" />
      <path d="M16 13.6c2.7 0 5 1.9 5 4.9h-4.2c0-1.9-.8-3.6-2.1-4.7.4-.1.9-.2 1.3-.2Z" fill="currentColor" opacity="0.6" />
    </Glyph>
  );
}

/** Transfers — arrows exchanging. */
export function IconTransfer({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path d="M7 5 3 9l4 4V10h9V8H7Z" fill="currentColor" />
      <path d="M17 19l4-4-4-4v3H8v2h9Z" fill="currentColor" opacity="0.75" />
    </Glyph>
  );
}

/** Insights — bar chart. */
export function IconInsights({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <rect x="4" y="12" width="4" height="8" rx="1" fill="currentColor" />
      <rect x="10" y="7" width="4" height="13" rx="1" fill="currentColor" opacity="0.8" />
      <rect x="16" y="4" width="4" height="16" rx="1" fill="currentColor" opacity="0.6" />
    </Glyph>
  );
}

/** Reverse — circular arrow. */
export function IconReverse({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path
        d="M12 5a7 7 0 1 1-6.7 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M12 2.5 8.5 5 12 7.5Z" fill="currentColor" />
    </Glyph>
  );
}

/** Notifications — bell. */
export function IconBell({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path
        d="M12 3a5 5 0 0 0-5 5v3.5L5.5 15h13L17 11.5V8a5 5 0 0 0-5-5Z"
        fill="currentColor"
      />
      <path d="M10 17a2 2 0 0 0 4 0Z" fill="currentColor" />
    </Glyph>
  );
}

/** Plus — add beneficiary / generic add. */
export function IconPlus({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6Z" fill="currentColor" />
    </Glyph>
  );
}

/* ---- Transaction category glyphs ---------------------------------------- */

/** Groceries / food — a shopping bag. */
export function IconGroceries({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path d="M6 8h12l-1 12H7L6 8Z" fill="currentColor" />
      <path d="M9 8a3 3 0 0 1 6 0" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </Glyph>
  );
}

/** Transport / fuel — a car. */
export function IconTransport({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path d="M5 13l1.5-4.5A2 2 0 0 1 8.4 7h7.2a2 2 0 0 1 1.9 1.5L19 13v5h-2v-2H7v2H5v-5Z" fill="currentColor" />
      <circle cx="8" cy="15" r="1.2" fill="#fff" />
      <circle cx="16" cy="15" r="1.2" fill="#fff" />
    </Glyph>
  );
}

/** Dining / restaurants — utensils. */
export function IconDining({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path d="M8 3v8m0 0v10m-2-18v6a2 2 0 0 0 4 0V3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
      <path d="M16 3c-1.7 0-3 2-3 5s1.3 4 3 4v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </Glyph>
  );
}

/** Bills / utilities — a receipt. */
export function IconBills({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path d="M6 3h12v18l-3-1.5L12 21l-3-1.5L6 21V3Z" fill="currentColor" />
      <path d="M9 8h6M9 12h6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </Glyph>
  );
}

/** Income / salary / credit — an upward trend. */
export function IconIncome({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path d="M4 16l5-5 3 3 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 8h5v5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Glyph>
  );
}

/** Entertainment / leisure — a play/ticket. */
export function IconEntertainment({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <rect x="3" y="6" width="18" height="12" rx="2" fill="currentColor" />
      <path d="M10 9.5v5l4-2.5Z" fill="#fff" />
    </Glyph>
  );
}

/** Chat — speech bubble. */
export function IconChat({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <path
        d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-4l-4 4v-4H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z"
        fill="currentColor"
      />
      <path d="M8 9h8M8 12h5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </Glyph>
  );
}

/** Fallback / uncategorised — a generic circle/tag. */
export function IconGeneric({ className }: GlyphProps): JSX.Element {
  return (
    <Glyph className={className}>
      <circle cx="12" cy="12" r="8" fill="currentColor" />
      <circle cx="12" cy="12" r="3" fill="#fff" />
    </Glyph>
  );
}
